import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography, Box, CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, TextField
} from '@mui/material';
import ApiServices from '../../Utils/ApiServices/ApiServices';
import AddMemberDialog from './AddMemberDialog';
import {
  getDecryptedAccessToken,
  getDecryptedData,
  getDecryptedUserType
} from '../../Utils/LoginUserDetails.js/LoginUserSavedDetails';
import GenerateMembershipBill from './GenerateMembershipBill';

const formatDate = (dateStr) => {
  if (!dateStr) return 'NA';
  return new Date(dateStr).toISOString().split('T')[0];
};

const PatientMembershipDetails = () => {
  const { groupId, plan_id } = useParams();
  const accessToken = getDecryptedAccessToken();
  const LoginUserDetails = getDecryptedData();
  const LoginUserType = getDecryptedUserType();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionData, setActionData] = useState({ open: false, action: '', member: null });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMembershipDetails = async () => {
    if (!groupId) return;

    const apiUrl =
      LoginUserType === 'Patient'
        ? `/apis/default/portal/membership/${groupId}/patient`
        : `/apis/default/api/membership/${groupId}/patient`;

    try {
      const res = await ApiServices.get(apiUrl, {
        Authorization: `Bearer ${accessToken}`
      });

      if (!res.data || res.data.length === 0) {
        setError(true);
      } else {
        setMembers(res.data);
        setError(false);
      }
    } catch (error) {
      console.error('Failed to fetch membership details:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembershipDetails();
  }, [groupId, accessToken, LoginUserType]);

  const handleApprovalAction = async () => {
    const { action, member } = actionData;
    const apiUrl =
      LoginUserType === 'Patient'
        ? `/apis/default/portal/membership/${groupId}/patient/${member.pid}`
        : `/apis/default/api/membership/${groupId}/patient/${member.pid}`;

    const payload = {
      status: action === 'approve' ? 'approved' : 'removed',
      admin_id: LoginUserDetails?.id,
    };

    try {
      await ApiServices.put(apiUrl, payload, {
        Authorization: `Bearer ${accessToken}`,
      });

      setSnackbar({
        open: true,
        message: `Member ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
        severity: 'success',
      });

      fetchMembershipDetails(); // refresh data
    } catch (err) {
      console.error('Approval/Rejection Error:', err);
      setSnackbar({
        open: true,
        message: `Failed to ${action} member`,
        severity: 'error',
      });
    } finally {
      setActionData({ open: false, action: '', member: null });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }
  const adminMember =
  members.some((m) => m.role === 'admin' && m.pid === LoginUserDetails.pid) ||
  LoginUserType != 'Patient';

  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Membership Group Members
      </Typography>

      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          label="Search by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ width: 300 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenConfirmDialog(true)}
          disabled={!adminMember}
        >
          Add Member
        </Button>
      </Box>

      {error ? (
        <Typography color="text.secondary" mt={4}>No members added yet.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Plan Tier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Joined Date</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members
                .filter(member =>
                  `${member.fname} ${member.lname}`.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((member, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{member.fname} {member.lname}</TableCell>
                    <TableCell>{member.age}</TableCell>
                    <TableCell>{member.plan_tier}</TableCell>
                    <TableCell>
                      <Chip
                        label={member.approval_status}
                        color={member.approval_status === 'approved' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.active ? 'Active' : 'Inactive'}
                        color={member.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(member.created_at)}</TableCell>
                    <TableCell>{formatDate(member.start_date)}</TableCell>
                    <TableCell>{formatDate(member.end_date)}</TableCell>
                    <TableCell>{member.price_applied ?? 'NA'}</TableCell>
                    <TableCell>{member.discount_applied ?? 'NA'}</TableCell>
                    <TableCell>
                      {member.approval_status === 'pending' && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => setActionData({ open: true, action: 'approve', member })}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setActionData({ open: true, action: 'reject', member })}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddMemberDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onReferralCreated={fetchMembershipDetails}
        accessToken={accessToken}
        LoginUserType={LoginUserType}
        plan_id={plan_id}
        groupId={groupId}
        setSnackbar={setSnackbar}
      />

<GenerateMembershipBill members={members}  onPaymentSuccess={fetchMembershipDetails}/>

      <Dialog open={actionData.open} onClose={() => setActionData({ open: false, action: '', member: null })}>
        <DialogTitle>Confirm {actionData.action === 'approve' ? 'Approval' : 'Rejection'}</DialogTitle>
        <DialogContent>
          Are you sure you want to {actionData.action} this member?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionData({ open: false, action: '', member: null })}>Cancel</Button>
          <Button variant="contained" color={actionData.action === 'approve' ? 'success' : 'error'} onClick={handleApprovalAction}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientMembershipDetails;
